import React, { createContext, useContext, useState, useCallback } from 'react';

interface DocumentFile {
  id: string;
  name: string;
  data: string; // base64
  mimeType: string;
}

interface DocumentContextType {
  documents: Record<string, DocumentFile>;
  addDocument: (doc: DocumentFile) => void;
  clearDocuments: () => void;
  getDocument: (id: string) => DocumentFile | undefined;
}

const DocumentContext = createContext<DocumentContextType | undefined>(undefined);

export const DocumentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [documents, setDocuments] = useState<Record<string, DocumentFile>>({});

  const addDocument = useCallback((doc: DocumentFile) => {
    setDocuments(prev => ({ ...prev, [doc.id]: doc }));
  }, []);

  const getDocument = useCallback((id: string) => {
    return documents[id];
  }, [documents]);

  const clearDocuments = useCallback(() => {
    setDocuments({});
  }, []);

  return (
    <DocumentContext.Provider value={{ documents, addDocument, clearDocuments, getDocument }}>
      {children}
    </DocumentContext.Provider>
  );
};

export const useDocuments = () => {
  const context = useContext(DocumentContext);
  if (context === undefined) {
    throw new Error('useDocuments must be used within a DocumentProvider');
  }
  return context;
};
